import { IconNames } from '@blueprintjs/icons';
import { skipToken } from '@reduxjs/toolkit/query';
import { formatDate } from '@strata/helper-functions';
import { type ColumnDef, type ColumnDefGeneric, Grid, SecondaryData } from '@strata/react-grid';
import { MessagingBlock } from '@strata/react-messaging-block';
import classNames from 'classnames';

import { ContingentTherapyRow } from 'components/treatments/ContingentTherapyRow';
import { useGetPrescriberDetailsQuery } from 'store/api/referenceApi';
import { type TreatmentAuth } from 'store/api/treatmentApi';

const CONTINGENT_THERAPY_ROW = 'CONTINGENT_THERAPY_ROW';

const initialSortProperties = [{ id: 'authStart', desc: true }];

/* eslint-disable react/prop-types */
const NPICell: ColumnDefGeneric<TreatmentAuth, 'requestingProvider'>['cell'] = ({ getValue }) => {
    const npi = getValue()?.trim();
    const { data: prescriberDetails } = useGetPrescriberDetailsQuery(npi ? { npi } : skipToken);

    const getDisplayText = (npiVal: string | undefined) => {
        if (prescriberDetails?.name) {
            return `${prescriberDetails.name} (${npiVal})`;
        }
        return npiVal;
    };

    return <SecondaryData data={getDisplayText(npi)} />;
};
/* eslint-enable react/prop-types */

const columns: ColumnDef<TreatmentAuth>[] = [
    {
        accessorKey: 'authStart',
        header: 'Auth Start',
        size: 110,
        cell: ({ getValue, row }) =>
            row.original.caseId === CONTINGENT_THERAPY_ROW ? (
                <ContingentTherapyRow />
            ) : (
                <SecondaryData data={formatDate(getValue())} />
            ),
    },
    {
        accessorKey: 'authExpiration',
        header: 'Auth End',
        size: 110,
        cell: ({ getValue }) => <SecondaryData data={formatDate(getValue())} />,
    },
    {
        accessorKey: 'caseId',
        header: 'Auth ID',
        size: 150,
        enableSorting: false,
    },
    {
        accessorKey: 'requestingProvider',
        header: 'Requesting Provider',
        size: 150,
        enableSorting: false,
        cell: NPICell,
    },
];

interface AuthDetailsProps {
    auths?: TreatmentAuth[];
    isContingentTherapy?: boolean;
    isLoading?: boolean;
}

export const AuthDetails = ({
    auths = undefined,
    isContingentTherapy = false,
    isLoading = true,
}: AuthDetailsProps) => {
    const gridData = [
        // Displays the Contingent Therapy row pinned to the top of the grid
        ...(isContingentTherapy
            ? [{ caseId: CONTINGENT_THERAPY_ROW, pinTop: true } as unknown as TreatmentAuth]
            : []),
        ...(auths ?? []),
    ];
    return (
        <Grid
            data={gridData}
            isLoading={isLoading}
            sortProperties={initialSortProperties}
            noResults={
                <div className="padding__around--large">
                    <MessagingBlock
                        size={MessagingBlock.Sizes.SMALL}
                        icon={IconNames.DOCUMENT}
                        primaryText="No authorizations available"
                    />
                </div>
            }
            rowCssClass={(rowData) =>
                classNames({
                    'griddle-row--contingent-therapy': rowData.caseId === CONTINGENT_THERAPY_ROW,
                })
            }
            columns={columns}
        />
    );
};
